import { useRef, useState } from 'react';
import { Button, FormInput, FormSelect } from '@components/UI';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import type { ScreenCursor, ScreenEntityTypeFilter, ScreenQualifiedEntity, ScreenUniversePageResponse } from '../types/screenerEditor';
import { buildScreenInput, downloadTextFile, parseOptionalInt, toCsv } from '../utils/screenerEditorUtils';

interface RunScreenSectionProps {
    screenerRuleId: number;
}

const RunScreenSection = ({ screenerRuleId }: RunScreenSectionProps) => {
    const [entityTypeFilter, setEntityTypeFilter] = useState<ScreenEntityTypeFilter>('all');
    const [asOfTimestamp, setAsOfTimestamp] = useState('');
    const [pageSize, setPageSize] = useState('1000');
    const [response, setResponse] = useState<ScreenUniversePageResponse | null>(null);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [isExportingAll, setIsExportingAll] = useState(false);
    const [exportProgress, setExportProgress] = useState<{ pagesFetched: number; entitiesFetched: number } | null>(null);
    const exportRunIdRef = useRef(0);

    const screenMutation = useTrpcMutation(trpc.screener.screenUniversePage.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to screen universe'
    });

    const runScreenPage = async (config: { cursor: ScreenCursor | null; reset: boolean }) => {
        const tsParsed = parseOptionalInt(asOfTimestamp, { label: 'As-Of Timestamp', min: 0, allowZero: true });

        if (!tsParsed.ok) {
            showToast(tsParsed.error, 'error');
            return;
        }

        const pageSizeParsed = parseOptionalInt(pageSize, { label: 'Page Size', min: 1, max: 5000, allowZero: false });

        if (!pageSizeParsed.ok) {
            showToast(pageSizeParsed.error, 'error');
            return;
        }

        try {
            const input = buildScreenInput({
                screenerRuleId,
                asOfTimestamp: tsParsed.value,
                entityTypeFilter,
                pageSize: pageSizeParsed.value,
                cursor: config.cursor
            });

            const result = await screenMutation.mutateAsync(input);
            setResponse(result.response);
            setErrorMessages(result.errorMessages ?? []);
            setPageIndex((prev) => (config.reset ? 0 : prev + 1));
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handleExportAll = async () => {
        const tsParsed = parseOptionalInt(asOfTimestamp, { label: 'As-Of Timestamp', min: 0, allowZero: true });

        if (!tsParsed.ok) {
            showToast(tsParsed.error, 'error');
            return;
        }

        const pageSizeParsed = parseOptionalInt(pageSize, { label: 'Page Size', min: 1, max: 5000, allowZero: false });

        if (!pageSizeParsed.ok) {
            showToast(pageSizeParsed.error, 'error');
            return;
        }

        setIsExportingAll(true);
        setExportProgress({ pagesFetched: 0, entitiesFetched: 0 });

        const collected: ScreenQualifiedEntity[] = [];
        let cursor: ScreenCursor | null = null;
        let pageCount = 0;
        const maxPages = 200;
        const maxEntities = 100000;
        const runId = ++exportRunIdRef.current;
        let hadError = false;
        let aborted = false;

        try {
            while (pageCount < maxPages && collected.length < maxEntities) {
                if (exportRunIdRef.current !== runId) {
                    aborted = true;
                    break;
                }

                const input = buildScreenInput({
                    screenerRuleId,
                    asOfTimestamp: tsParsed.value,
                    entityTypeFilter,
                    pageSize: pageSizeParsed.value,
                    cursor
                });

                const result = await screenMutation.mutateAsync(input);

                if (!result.success) {
                    const messages = result.errorMessages ?? [];
                    showToast(messages.length > 0 ? messages.join('; ') : 'Export failed: screener returned an error', 'error');
                    hadError = true;
                    break;
                }

                const pageEntities = result.response.qualifiedEntities;
                collected.push(...pageEntities);
                cursor = result.response.nextCursor;
                pageCount += 1;
                setExportProgress({ pagesFetched: pageCount, entitiesFetched: collected.length });

                if (result.response.isDone) break;
            }

            if (pageCount >= maxPages) {
                showToast(`Export stopped at maxPages=${maxPages}`, 'error');
            } else if (collected.length >= maxEntities) {
                showToast(`Export stopped at maxEntities=${maxEntities}`, 'error');
            }
        } catch {
            hadError = true;
        } finally {
            setIsExportingAll(false);
            setExportProgress(null);
        }

        if (aborted || hadError) return;

        downloadTextFile({
            filename: `screener-${screenerRuleId}-export.csv`,
            content: toCsv(
                collected.map((e) => ({
                    entityType: e.entityType,
                    entityId: e.entityId,
                    symbol: e.symbol,
                    name: e.name
                }))
            ),
            mimeType: 'text/csv'
        });
    };

    return (
        <div>
            <div className="text-sm text-zinc-400 mb-3">Run the screener against the universe with pagination.</div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormSelect
                    label="Entity Type Filter"
                    options={[
                        { value: 'all', label: 'All (allowed)' },
                        { value: 'Asset', label: 'Asset' },
                        { value: 'Cryptocurrency', label: 'Cryptocurrency' },
                        { value: 'AssetIndex', label: 'Index' }
                    ]}
                    value={entityTypeFilter}
                    onChange={(e) => setEntityTypeFilter(e.target.value as ScreenEntityTypeFilter)}
                />
                <FormInput
                    label="As-Of Timestamp (seconds)"
                    placeholder="0 = now"
                    value={asOfTimestamp}
                    onChange={(e) => setAsOfTimestamp(e.target.value)}
                />
                <FormInput label="Page Size (1-5000)" value={pageSize} onChange={(e) => setPageSize(e.target.value)} />
                <div className="flex items-end gap-2">
                    <Button
                        disabled={screenMutation.isPending || isExportingAll}
                        onClick={async () => {
                            setResponse(null);
                            setErrorMessages([]);
                            setPageIndex(0);
                            await runScreenPage({ cursor: null, reset: true });
                        }}
                    >
                        {screenMutation.isPending ? 'Running...' : 'Start'}
                    </Button>
                    <Button
                        disabled={screenMutation.isPending || isExportingAll || !response || response.isDone}
                        variant="secondary"
                        onClick={async () => {
                            if (!response) return;
                            await runScreenPage({ cursor: response.nextCursor, reset: false });
                        }}
                    >
                        Next
                    </Button>
                </div>
            </div>

            {errorMessages.length > 0 && (
                <div className="mt-3 text-xs text-red-400">
                    {errorMessages.map((m, idx) => (
                        <div key={idx}>{m}</div>
                    ))}
                </div>
            )}

            {response && (
                <div className="mt-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="text-xs text-zinc-400">
                            page={pageIndex} qualifiedEntities={response.qualifiedEntities.length} done=
                            {response.isDone ? 'yes' : 'no'} asOfTimestampUsed={response.asOfTimestampUsed}
                        </div>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                                downloadTextFile({
                                    filename: `screener-${screenerRuleId}-page-${pageIndex}.csv`,
                                    content: toCsv(
                                        response.qualifiedEntities.map((e) => ({
                                            entityType: e.entityType,
                                            entityId: e.entityId,
                                            symbol: e.symbol,
                                            name: e.name
                                        }))
                                    ),
                                    mimeType: 'text/csv'
                                })
                            }
                        >
                            Export Page CSV
                        </Button>
                        <Button
                            disabled={isExportingAll || screenMutation.isPending}
                            size="sm"
                            variant="secondary"
                            onClick={handleExportAll}
                        >
                            Export All CSV
                        </Button>
                        {isExportingAll && (
                            <Button
                                size="sm"
                                variant="danger"
                                onClick={() => {
                                    exportRunIdRef.current += 1;
                                }}
                            >
                                Cancel Export
                            </Button>
                        )}
                        {exportProgress && (
                            <div className="text-xs text-zinc-400">
                                exporting... pages={exportProgress.pagesFetched} entities={exportProgress.entitiesFetched}
                            </div>
                        )}
                    </div>

                    <div className="text-xs text-zinc-400">
                        missingDataKeys: {response.missingDataKeys.length ? response.missingDataKeys.join(', ') : '—'}
                    </div>

                    <div className="border border-zinc-700 rounded overflow-hidden">
                        <div className="max-h-[520px] overflow-auto">
                            <table className="min-w-full text-xs">
                                <thead className="bg-zinc-900/40 text-zinc-400">
                                    <tr>
                                        <th className="text-left px-3 py-2">Type</th>
                                        <th className="text-left px-3 py-2">ID</th>
                                        <th className="text-left px-3 py-2">Symbol</th>
                                        <th className="text-left px-3 py-2">Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {response.qualifiedEntities.map((e) => (
                                        <tr className="border-t border-zinc-800" key={`${e.entityType}:${e.entityId}`}>
                                            <td className="px-3 py-2">{e.entityType}</td>
                                            <td className="px-3 py-2">{e.entityId}</td>
                                            <td className="px-3 py-2">{e.symbol ?? '—'}</td>
                                            <td className="px-3 py-2 truncate max-w-[420px]">{e.name ?? '—'}</td>
                                        </tr>
                                    ))}
                                    {response.qualifiedEntities.length === 0 && (
                                        <tr>
                                            <td className="px-3 py-3 text-zinc-400" colSpan={4}>
                                                No qualified entities on this page.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RunScreenSection;
